import React from 'react';
import classNames from 'classnames';

import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import { Display } from 'Views/Components/Button/Button';
import { usesTwelveHourClock } from 'Util/_HumanWritten/TimeUtils';
import { deselectProps, tripOptionStateClasses } from './tripOptionState';
import TripOptionChangeButton from './TripOptionChangeButton';
import {
	TripOptionChosenLabel,
	TripOptionPrice,
	TripOptionAside,
	TripOptionSoldOutNote,
	TripOptionSpace,
	TripOptionTimes,
	TripOptionVoyage,
	TripOptionWhen,
} from './TripOptionParts';

/**
 * A sailing at desktop width — `2a` in the list, `4a` once chosen.
 *
 * <p>Both are the same four bands across the row: the availability rail, the times, what is left,
 * and the price. The chosen one widens the first two and takes the day with it, because by then the
 * question has moved from "which of these" to "is this the one I meant".</p>
 */
export default function TripOptionWeb() {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		console.error('LegOptionContext is missing in TripOptionWeb');
		return null;
	}

	const { isSelected, isSelectable, onClick } = context;

	if (isSelected) {
		return (
			<div
				className="trip-option-shell trip-option-shell--selected"
				data-test-id="trip-option-selected"
				data-trip-id={context.trip.id}
			>
				<div
					className="trip-option trip-option--web trip-option--selected"
					{...deselectProps(onClick)}
				>
					<div className="trip-option__rail" aria-hidden="true" />

					<div className="trip-option__trip">
						<TripOptionChosenLabel />
						<TripOptionTimes size="lg" />
						<span className="trip-option__when-row">
							<TripOptionWhen />
							<TripOptionSoldOutNote inline />
						</span>
					</div>

					<div className="trip-option__panel">
						<div className="trip-option__panel-row">
							<TripOptionSpace />
							<TripOptionAside>
								<TripOptionPrice size="lg" />
							</TripOptionAside>
						</div>
					</div>
				</div>

				<div className="trip-option__footer">
					<TripOptionChangeButton display={Display.Text} />
				</div>
			</div>
		);
	}

	return (
		<div
			className={classNames('trip-option', 'trip-option--web', tripOptionStateClasses(context), {
				'trip-option--twelve-hour': usesTwelveHourClock(),
			})}
			data-test-id={`trip-option-${context.trip.id}`}
			data-trip-selectable={isSelectable ? 'true' : 'false'}
			data-trip-availability={context.availabilityStatus ?? 'unknown'}
			onClick={isSelectable ? onClick : undefined}
			onKeyDown={event => {
				if (isSelectable && (event.key === 'Enter' || event.key === ' ')) {
					event.preventDefault();
					onClick?.();
				}
			}}
			role="button"
			tabIndex={isSelectable ? 0 : -1}
			aria-disabled={!isSelectable}
		>
			<div className="trip-option__rail" aria-hidden="true" />

			<div className="trip-option__trip">
				<TripOptionVoyage />
				<TripOptionTimes size="md" />
				<TripOptionSoldOutNote />
			</div>

			<TripOptionSpace />

			<TripOptionAside>
				<TripOptionPrice size="md" />
			</TripOptionAside>
		</div>
	);
}
